import Moment from "moment";

import config from "@/config";
import store from "@/store/index";
import i18n from "@/modules/i18n";

/**
 * Récupère le code de langage du navigateur.
 * @param {Object} options Contient l'option countryCodeOnly si on souhaite seulement le code.
 * @returns {String} Code du langage du navigateur.
 */
export function getBrowserLocale(options = {}) {
  const defaultOptions = { countryCodeOnly: false };
  const opt = { ...defaultOptions, ...options };
  const navigatorLocale = navigator.languages !== undefined
    ? navigator.languages[0]
    : navigator.language;
  if (!navigatorLocale) {
    return undefined;
  }
  const trimmedLocale = opt.countryCodeOnly
    ? navigatorLocale.trim().split(/-|_/)[0]
    : navigatorLocale.trim();
  return trimmedLocale;
}

/**
 * Nous dit si le local en paramètre est supporté par l'application.
 * @param {String} locale Code ISO de la langue.
 * @returns {Boolean} true si locale est supporté par l'application.
 */
export function supportedLocalesInclude(locale) {
  return Object.keys(config.supportedLocales).includes(locale);
}

/**
 * Permet de définir une couleur en fonction d'un taux donné.
 * @param {Number} num Mot ou phrase à transformer.
 * @param {Boolean} inversed Inverse l'ordre des couleurs.
 *                           Par défaut : rouge si >= 100%, ; si inversed : rouge si <= 0%.
 * @returns {String} Classe de la couleur associée.
 */
export function couleurSelonTaux(percent, inversed = false) {
  const isNormandie = store.getters.isNormandie;

  if (!inversed) {
    if (percent >= (isNormandie ? 80 : 90)) {
      if (percent >= 100) {
        return "negative";
      }
      return "warning";
    }
    return "positive";
  }

  if (percent <= (isNormandie ? 20 : 10)) {
    if (percent <= 0) {
      return "negative";
    }
    return "warning";
  }
  return "positive";
}
/**
 * Avertit l'utilisateur qu'il n'est pas sur le serveur de production selon son action.
 * @returns {Boolean}
 */
export function confirmOrganismeOnTesting() {
  const isOnProd = [
    "numerique.lde.fr", "cristal-welt.lde.de", "univers-cristal.lde.fr",
  ].includes(window.location.host);

  if (!isOnProd) {

    const idClient = prompt(i18n.global.t("general.pas-production-confirmation"), "");
    if (idClient && idClient.toLowerCase() === store.getters.organismeActuel?.id_organisme.toLowerCase()) {
      return true;
    }

    alert(i18n.global.t("general.pas-bon-id-client"));
  } else {
    return true;
  }
  return false;
}

/**
 * Affiche un poids de fichier avec l'unité la plus lisible.
 * @param {Number} bytes Taille en octets.
 */
export function convertSizeFile(bytes) {
  const units = [
    i18n.global.t("unites.octets"),
    i18n.global.t("unites.ko"),
    i18n.global.t("unites.mo"),
    i18n.global.t("unites.go"),
    i18n.global.t("unites.to"),
  ];

  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  return i === 0 ? `${bytes} ${units[i]}` : `${(bytes / (1024 ** i)).toFixed(1)} ${units[i]}`;
}

/**
 * Vérifie si un fichier est correct.
 * @param {Object} file Contient les données du fichier.
 * @param {Object} conf Données de config à valider pour le fichier.
 * @returns {Promise}
 */
export function checkFile(file, conf) {
  const { maxSizeFile, accept, acceptedStr } = conf;

  return new Promise((resolve, reject) => {
    if (file && file.length === 1) {
      const { type, size } = file[0];
      // Vérifier le format
      if (!accept.includes(type)) {
        // Une erreur de type
        const str = `${acceptedStr.slice(0, -1).join(", ")} ou ${acceptedStr.slice(-1)}`;
        reject(new Error(i18n.global.t("piece-jointe.le-type-du-fichier-nest-pas-correct-il-doit-etre-un-x", [str])));
      } else if (size === 0) {
        // Si pas d'image
        reject(new Error(i18n.global.t("piece-jointe.image-ne-peut-pas-etre-vide")));
      } else if (convertSizeFile(size) > maxSizeFile) {
        // Si image trop lourde
        reject(new Error(i18n.global.t("piece-jointe.la-taille-maximal-de-limage-est-de-x-ko", [maxSizeFile])));
      } else {
        resolve(file[0]);
      }
    } else {
      reject(new Error(i18n.global.t("piece-jointe.plusieurs-fichiers-selectionnes")));
    }
  });
}

/**
 * Change le route.meta.title ainsi que le document.title.
 * @param {String} title Titre à afficher.
 * @param {Object} route Route actuelle du router ($route ne peut pas être importé donc on le passe en param).
 * @param {Boolean} onlyDocumentTitle Ne pas changer le meta title (qui est affiché dans le PageContent)
 */
export function changePageTitle(title, route, onlyDocumentTitle) {
  if (route.name === "home" || !title) {
    document.title = `${i18n.global.t("general.univers-cristal")} - LDE`;
  } else {
    document.title = `${title} - ${i18n.global.t("general.univers-cristal")} - LDE`;
  }

  if (route && title && !onlyDocumentTitle) {
    route.meta.title = () => title;
  }
}

/**
 * Vérifie si les desiderata (livraison) sont consultables pour ce maître site (conditions
 * marché / pays / fenêtre de dates), indépendamment de la permission de modification.
 * @param {Object} maitreSite Maître site du compte courant. Le compte courant peut être le maître site.
 * @returns {Boolean}
 */
export function canViewDesiderata(maitreSite) {
  if (maitreSite) {
    const isGMBH = store.getters.isGMBH;
    const date = Moment();
    const year = date.year();
    let canAccessDate = false;
    let estMarcheCompatible = false;
    let paysConcerne = "france";

    // Les dates de livraison allemandes sont différentes ainsi que les condtions d'accès.
    if (isGMBH) {
      if (!maitreSite.infos.bundesland) {
        return false;
      }
      canAccessDate = date.isBetween(`${year}-04-01`, `${year}-09-30`, "days", "[]");
      paysConcerne = "deutschland";
      estMarcheCompatible = true;
    } else {
      canAccessDate = date.isBetween(`${year}-05-29`, `${year}-11-01`, "days", "[]");
      estMarcheCompatible = (
        store.getters.isHorsMarche
        && !maitreSite.marches?.length
        && maitreSite.infos?.categorie?.toLowerCase() !== "primaire"
      );
    }

    return canAccessDate
      && estMarcheCompatible
      && !maitreSite.infos.exclus_du_1500
      && maitreSite?.pays_livraison?.toLowerCase() === paysConcerne
      && maitreSite?.pays_facturation?.toLowerCase() === paysConcerne;
  }
  return false;
}

/**
 * Vérifie si on a le droit de modifier les desiderata (livraison) : conditions de consultation
 * + permission d'écriture.
 * @param {Object} maitreSite Maître site du compte courant. Le compte courant peut être le maître site.
 * @returns {Boolean}
 */
export function canAccessDesiderata(maitreSite) {
  return canViewDesiderata(maitreSite) && store.getters.hasPerm("can_set_dates_livraison");
}

/**
 * Client scolaire France (hors-marché) éligible à l'affichage des infos de livraison.
 * @param {Object} maitreSite
 * @returns {Boolean}
 */
function estScolaireFranceEligible(maitreSite) {
  return store.getters.isHorsMarche
    && !maitreSite.marches?.length
    && maitreSite?.pays_livraison?.toLowerCase() === "france"
    && maitreSite?.pays_facturation?.toLowerCase() === "france";
}

/**
 * Détermine le mode d'affichage des infos de livraison sur la fiche établissement.
 * - "rentree" : desiderata / date d'expédition de rentrée (jusqu'au 04/09 au soir, primaires inclus).
 * - "expedition" : dernière + prochaine expédition planifiée (à compter du 05/09 pour tous, et
 *   toute l'année pour les clients exclus du 1500).
 * - null : rien à afficher.
 * @param {Object} maitreSite Maître site du compte courant.
 * @returns {"rentree"|"expedition"|null}
 */
export function modeAffichageLivraison(maitreSite) {
  if (!maitreSite) {
    return null;
  }

  // Allemagne (GMBH) : comportement existant, desiderata de rentrée uniquement.
  if (store.getters.isGMBH) {
    return canViewDesiderata(maitreSite) ? "rentree" : null;
  }

  if (!estScolaireFranceEligible(maitreSite)) {
    return null;
  }

  // Clients exclus du 1500 : affichage expédition toute l'année.
  if (maitreSite.infos.exclus_du_1500) {
    return "expedition";
  }

  const now = Moment();
  const year = now.year();
  // Période de rentrée jusqu'au 04/09 au soir ; à compter du 05/09, bascule sur l'affichage expédition.
  if (now.isBetween(`${year}-05-29`, `${year}-09-04`, "days", "[]")) {
    return "rentree";
  }

  return "expedition";
}

/**
 * Permet d'accéder dynamiquement à une propriété en profondeur dans un objet.
 * @param {Object} obj Objet sur lequel on veut récuprer une valeur.
 * @param {String} keys Clés imbriquées de l'objet.
 * @returns {?} Valeur associée à la clé (peut être de tous les types).
 */
export function getNestedProperty(obj, keys) {
  let result = obj;
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (result === undefined) {
      // Gérer le cas où la propriété n'existe pas
      return undefined;
    }
    result = result[key];
  }
  return result;
}

/**
 * Trie les données du tableau selon la valeur choisie.
 * @param {Object} sortAscended Si true, le tri est croissant.
 * @param {Object} key Clé sur laquelle trier.
 */
export function handleSort({ sortAscended, key }) {
  this.$router.push({
    query: {
      ...this.$route.query,
      sort: `${sortAscended ? "" : "-"}${key}`,
      page: 1,
    },
  });
}

/**
 * Créer une promesse différée.
 * @returns {Object} Promesse différée.
 */
export function createDeferredPromise() {
  let resolve;
  let reject;

  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

/**
 * Indique si une configuration multi-domaines (domaine public distinct du domaine connecté) est active.
 * @returns {Boolean}
 */
export function hasSeparatePublicDomain() {
  if (!config.url.cataloguePublicUrl) return false;
  try {
    const publicUrl = new URL(config.url.cataloguePublicUrl, window.location.origin);
    const connectedUrl = config.url.connectedUrl
      ? new URL(config.url.connectedUrl, window.location.origin)
      : null;

    if (connectedUrl) {
      return publicUrl.hostname !== connectedUrl.hostname;
    }
    return publicUrl.hostname !== window.location.hostname;
  } catch (e) {
    return false;
  }
}

/**
 * Indique si la page courante s'exécute sur le domaine dédié au catalogue public.
 * Ne retourne true QUE s'il y a un domaine public distinct ET que le hostname actuel correspond.
 * @returns {Boolean}
 */
export function isOnPublicDomain() {
  if (!config.url.cataloguePublicUrl) return false;
  try {
    const publicUrl = new URL(config.url.cataloguePublicUrl, window.location.origin);
    return hasSeparatePublicDomain() && window.location.hostname === publicUrl.hostname;
  } catch (e) {
    return false;
  }
}

/**
 * Retourne l'URL de l'espace connecté (pour redirection depuis le domaine public).
 * @returns {String}
 */
export function getConnectedUrl() {
  return config.url.connectedUrl || config.loginUrl || window.location.origin;
}

/**
 * Calcule le domaine racine (ex: .lde.fr) pour le partage de cookies cross-sous-domaines.
 * @returns {String}
 */
export function getRootDomain() {
  const parts = window.location.hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }
  return window.location.hostname;
}

/**
 * Enregistre ou supprime la préférence de masquage de la modale d'import panier.
 * @param {Boolean} hide
 */
export function setHideModalImportPanier(hide) {
  const domain = getRootDomain();
  if (hide) {
    localStorage.setItem("hide_modal_import_panier", "true");
    document.cookie = `hide_modal_import_panier=true; domain=${domain}; path=/; max-age=${30 * 24 * 60 * 60}`;
  } else {
    localStorage.removeItem("hide_modal_import_panier");
    // Supprime le cookie cross-domaine (.lde.fr) ainsi que le cookie host-only (dev/localhost)
    document.cookie = `hide_modal_import_panier=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = "hide_modal_import_panier=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

/**
 * Vérifie si la modale d'import panier a été marquée comme masquée.
 * Vérifie d'abord le localStorage du domaine courant, puis le cookie partagé (.lde.fr).
 * @returns {Boolean}
 */
export function isHideModalImportPanierSet() {
  if (localStorage.getItem("hide_modal_import_panier") === "true") {
    return true;
  }
  const match = document.cookie.match(/(?:^|; )\s*hide_modal_import_panier=([^;]*)/);
  return Boolean(match && match[1] === "true");
}

/**
 * Convertit la valeur d'un filtre dans l'URL (« 12,45 ») en options.
 * Les options sont comparées aux ids numériques du back ; une valeur non numérique reste telle quelle.
 * @param {string|number} value Valeur de la query string.
 * @returns {Array<number|string>} Options du filtre.
 */
export function filterOptionsFromQuery(value) {
  return value.toString().split(",").map((option) => {
    const numberValue = Number(option);
    return Number.isNaN(numberValue) ? option : numberValue;
  });
}

/**
 * Convertit une sélection de filtres en paramètres d'URL.
 * @param {Array<Object>} groups Groupes { slug, options }.
 * @returns {Object} Filtres sous la forme { slug: "12,45" }.
 */
export function filterGroupsToQuery(groups) {
  return Object.fromEntries(groups.map(({ slug, options }) => [slug, options.join(",")]));
}

/**
 * Indique si une sélection de filtres est déjà celle appliquée dans l'URL.
 * Valider une sélection identique ne doit rien relancer : même l'ajout de `page=1` suffirait
 * à déclencher une nouvelle recherche.
 * @param {Object} routeQuery Query string courante.
 * @param {Array<string>} filterSlugs Slugs des filtres gérés par le formulaire.
 * @param {Object} newFilters Filtres à appliquer, sous la forme { slug: "12,45" }.
 * @returns {boolean} Vrai si l'URL porte déjà exactement ces filtres.
 */
export function isSameFilterQuery(routeQuery, filterSlugs, newFilters) {
  const normalize = (entries) => JSON.stringify(
    entries
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([slug, value]) => [slug, value.toString().split(",").sort()])
      .sort(([slugA], [slugB]) => slugA.localeCompare(slugB)),
  );
  const appliedFilters = Object.entries(routeQuery).filter(([key]) => filterSlugs.includes(key));

  return normalize(appliedFilters) === normalize(Object.entries(newFilters));
}
